/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import React from 'react';
import { Link } from 'wouter';

import styles from '../../styles/buttons/button.module.css';

interface ButtonProps {
    type?: string | null;
    href?: string | null;
    external?: boolean;
    direct?: boolean;
    target?: string;
    label?: React.ReactNode;
    children?: React.ReactNode;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right' | 'inline';
    disabled?: boolean;
    loading?: boolean;
    disableOnLoading?: boolean;
    style?: Record<string, any> | null;
    className?: string | null;
    iconClassName?: string | null;
    labelClassName?: string | null;
    onClick?: (() => void) | null;
}

function Button({
    type = 'button',
    href = null,
    external = false,
    direct = false,
    target = '_blank',
    label = null,
    children = null,
    icon = null,
    iconPosition = 'inline',
    disabled = false,
    loading = false,
    disableOnLoading = true,
    onClick = null,
    style = null,
    className = null,
    iconClassName = null,
    labelClassName = null,
}: ButtonProps) {
    const finalLabel = label || children;
    const text = finalLabel;
    const hasChildren = label !== null && children !== null;
    const hasIcon = icon !== null;
    const hasInlineIcon = hasIcon && (iconPosition === 'inline' || text === null);
    const hasIconColumns = hasIcon && !hasInlineIcon;
    const content = (
        <>
            {hasInlineIcon ? (
                <>
                    <span
                        className={classNames([
                            styles.icon,
                            {
                                [iconClassName]: iconClassName !== null,
                            },
                        ])}
                    >
                        {icon}
                    </span>
                    {text !== null ? (
                        <span
                            className={classNames([
                                styles.label,
                                {
                                    [labelClassName]: labelClassName !== null,
                                },
                            ])}
                        >
                            {text}
                        </span>
                    ) : null}
                </>
            ) : null}
            {hasIconColumns ? (
                <>
                    <span className={classNames([styles.left])}>
                        {iconPosition === 'left' ? icon : null}
                    </span>
                    <span className={classNames([styles.center])}>{text}</span>
                    <span className={classNames([styles.right])}>
                        {iconPosition === 'right' ? icon : null}
                    </span>
                    {hasChildren ? children : null}
                </>
            ) : null}
            {!hasIcon ? finalLabel : null}
            {hasChildren ? children : null}
        </>
    );

    const buttonClassNames = classNames([
        styles.container,
        {
            [styles.withIcon]: hasIcon,
            [styles.withIconColumns]: hasIconColumns,
            [styles.withText]: text !== null,
            [styles.isLink]: href !== null,
            [styles.isDisabled]: disabled,
            [styles.isLoading]: loading,
            [className]: className !== null,
        },
    ]);

    if (href !== null) {
        return external || direct ? (
            <a
                href={href}
                className={buttonClassNames}
                onClick={onClick}
                target={external ? target : null}
                style={style}
            >
                {content}
            </a>
        ) : (
            <Link href={href} className={buttonClassNames} onClick={onClick} style={style}>
                {content}
            </Link>
        );
    }
    return (
        <button
            type={type || 'button'}
            className={buttonClassNames}
            onClick={onClick}
            disabled={disabled || (disableOnLoading && loading)}
            style={style}
        >
            {content}
        </button>
    );
}

export default Button;
